import { Size } from '@ps/web-core-enums';
import type { TrackedTranslation } from '@ps/web-core-intl';
import { useTrackers } from '@ps/web-shared-analytics';
import { StarsButton, Translation, useScreenSize } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { interval } from 'rxjs';

import { TimedStatus } from '@swf/casino';
import { GADimension } from '@swf/core/enums';
import useCasinoBonusGame from '@swf/core/hooks/useCasinoBonusGame';
import { dispatch, select } from '@swf/core/store';
import { BonusGameTypeVariant, BonusStatus } from '@swf/core/types/CasinoBonus';
import type { SpinOfDayData } from '@swf/core/types/CasinoBonus';
import getBackgroundImageStyle from '@swf/core/utils/getBackgroundImageStyle';

import Clock from '../../../../assets/clock.svg';

import {
  TXTCLI_Play,
  TXTWCL_1_Spin,
  TXTWCL_N0_Spins,
  TXTWCL_XtitleXExpires_inXX_titleXXsub_ELL
} from './Ticket.messages.json';
import styles from './Ticket.scss';

interface Props {
  firstGameInWidget: SpinOfDayData;
  data: SpinOfDayData;
  layout: number;
}

const Ticket: React.FC<Props> = ({ firstGameInWidget, data, layout }) => {
  const shouldLaunchSotdFromBanner = useSelector(select.casino.shouldLaunchSotd);
  const isMobile = useSelector(select.device.isMobile);
  const { greaterThan } = useScreenSize();
  const useButton = !isMobile && greaterThan(Size.Small);
  const { status, numberOfSpins, expiryTime, theme, themeId, variantType } = data;
  const isAvailable = status === BonusStatus.Available;
  const [startTime, setStartTime] = useState<number>(Date.now());
  const trackingDimensions = {
    [GADimension.TabName]: 'Spin of the Day',
    [GADimension.CampaignID]: data.bonusCampaignId,
    [GADimension.TicketID]: data.bonusId
  };

  const themeStyles: Record<string, Record<string, string>> = {
    default: {
      spinStyle: styles.iconTitleTextSpin,
      dayStyle: styles.iconTitleText,
      secondaryStyle: styles.iconTitleText
    },
    BookOfDead: {
      spinStyle: styles.iconTitleTextBookofdead,
      dayStyle: styles.iconTitleTextBookofdead,
      secondaryStyle: styles.iconTitleTextBookofdead
    },
    mega: {
      spinStyle: styles.iconTitleText,
      dayStyle: styles.iconTitleText,
      secondaryStyle: styles.iconTitleText
    },
    SpainCampaign: {
      spinStyle: styles.iconTitleTextSpaincampaign,
      dayStyle: styles.iconTitleTextSpaincampaign,
      secondaryStyle: styles.iconSecondaryTextSpaincampaign
    }
  };

  const getStyles = () => {
    const unifiedThemeId = /mega/i.test(themeId) ? 'mega' : themeId;
    return themeStyles[unifiedThemeId] || themeStyles.default;
  };

  const { spinStyle, dayStyle, secondaryStyle } = getStyles();

  const launchGame = useCasinoBonusGame();
  const { buttonPressed } = useTrackers();
  const locale = useSelector(select.locale.get);

  const message = () => {
    const esLocalizedThemes = ['BookOfDead', 'Halloween', 'Xmas'];

    if (variantType === BonusGameTypeVariant.BonusRound) {
      if (locale === 'es') {
        return '<spin>RONDAS EXTRA</spin>';
      } else {
        return '<spin>BONUS ROUNDS</spin>';
      }
    } else {
      if (themeId === 'SpainCampaign' || (locale === 'es' && esLocalizedThemes.includes(themeId))) {
        return '<spin>EL</spin> <secondary>JUEGO</secondary> <day>DEL</day> <day>DÍA</day>';
      }
      if (locale === 'ro') {
        return '<spin>Rotirea</spin> <day>Zilei</day>';
      }
      return '<spin>Spin</spin> <secondary>of</secondary> <secondary>the</secondary> <day>Day</day>';
    }
  };

  const tileTitle: TrackedTranslation = {
    id: 'ID_DOES_NOT_EXIST',
    defaultMessage: message(),
    flags: ['HTML', 'TRACK'],
    track: 'Spin of the Day'
  };

  const launch = () => {
    if (data) {
      launchGame(data);
      isMobile && buttonPressed({ buttonName: 'Play' }, trackingDimensions);
    }
  };

  useEffect(() => {
    if (shouldLaunchSotdFromBanner) {
      launchGame(firstGameInWidget);
      dispatch.casino.set({ shouldLaunchSotd: false });
    }
  }, [shouldLaunchSotdFromBanner, firstGameInWidget, launchGame]);

  useEffect(() => {
    const base = interval(1000).subscribe(() => setStartTime(Date.now()));
    return () => base.unsubscribe();
  }, []);

  return (
    <div
      className={classNames(styles[('layout' + layout) as keyof typeof styles], styles.container)}
      onClick={!useButton && isAvailable ? launch : undefined}
    >
      <div className={styles.iconContainer} style={getBackgroundImageStyle(theme)}>
        <div className={styles.iconTile}>
          <Translation
            {...tileTitle}
            values={{
              spin: (text: ReactNode) => <span className={spinStyle}>{text}</span>,
              day: (text: ReactNode) => <span className={dayStyle}>{text}</span>,
              secondary: (text: ReactNode) => <span className={secondaryStyle}>{text}</span>
            }}
          />
        </div>
      </div>
      <div className={styles.infoContainer}>
        <div className={styles.spinInfo}>
          <Translation
            {...(numberOfSpins > 1 ? TXTWCL_N0_Spins : TXTWCL_1_Spin)}
            values={{ 0: numberOfSpins }}
          />
        </div>
        <div className={styles.infoBox}>
          <div className={styles.expiryInfo}>
            <Translation
              {...TXTWCL_XtitleXExpires_inXX_titleXXsub_ELL}
              values={{
                0: (
                  <TimedStatus
                    running
                    startTime={startTime}
                    endTime={expiryTime}
                    count={startTime}
                  />
                ),
                title: (text: ReactNode) => <span>{text}</span>,
                subtitle: (text: ReactNode) => (
                  <span className={styles.number}>
                    <Clock />
                    {text}
                  </span>
                )
              }}
            />
          </div>
          {useButton && (
            <div className={styles.actionButton}>
              <StarsButton
                onClick={launch}
                disabled={!isAvailable}
                size="small"
                className={styles.playButton}
                translation={TXTCLI_Play}
                trackingDimensions={trackingDimensions}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Ticket;
